import { makeAutoObservable } from "mobx";
import type { Member } from "@smartsuite/automation-engine-api";
import { mergeDirectoryEntries } from "./directory-merge";

export class MembersStore {
  /**
   * Every member loaded in this session, deduplicated. Use it to resolve a saved
   * member id to a name (pill labels, automation descriptions) -- NOT as the
   * source for a picker, which must be scoped: see `forSolution` / `workspace`.
   */
  list: Member[] = [];
  pageSize: number = 20;
  /**
   * Members keyed by the solution they were loaded for. Selectable members are
   * scoped by solution permissions, and an action can write into a solution
   * other than the one hosting the automation, so every referenced solution is
   * kept separately.
   */
  bySolution: Record<string, Member[]> = {};
  /**
   * Every member of the account, regardless of solution permissions. Used for
   * notification recipients, which don't need access to any solution.
   */
  workspace: Member[] = [];
  workspaceLoaded: boolean = false;

  constructor() {
    // `forSolution` must stay a plain method: makeAutoObservable would annotate
    // it as an action, and MobX actions run untracked, so an observer reading
    // members through it would not re-render when a solution finishes loading.
    makeAutoObservable(this, { forSolution: false, hasSolution: false });
  }

  setList(members: Member[]): void {
    this.bySolution = {};
    this.workspace = [];
    this.workspaceLoaded = false;
    this.list = members;
  }

  setForSolution(solutionId: string, members: Member[]): void {
    this.bySolution = { ...this.bySolution, [solutionId]: members };
    this.mergeList();
  }

  setWorkspace(members: Member[]): void {
    this.workspace = members;
    this.workspaceLoaded = true;
    this.mergeList();
  }

  /** Members selectable on a field that writes into `solutionId`. */
  forSolution(solutionId: string): Member[] {
    return this.bySolution[solutionId] ?? [];
  }

  hasSolution(solutionId: string): boolean {
    return solutionId in this.bySolution;
  }

  reset(): void {
    this.bySolution = {};
    this.workspace = [];
    this.workspaceLoaded = false;
    this.list = [];
  }

  private mergeList(): void {
    this.list = mergeDirectoryEntries(
      [...Object.values(this.bySolution), this.workspace],
      (member) => member.memberId,
    );
  }
}
